/**
 * RU-подписи раздела «Контент» — единственный источник для экранов
 * `admin-module-content`. Переводится только отображаемая строка, машинные
 * значения контракта не меняются.
 *
 * Аннотация `Record<Union, string>` — это и есть проверка полноты: новое
 * значение в `admin-contracts/src/content.ts` без подписи здесь ломает сборку,
 * а не показывает оператору `undefined` или `no_data`.
 *
 * Тексты — простые, без терминов движка («политика», «hook», «ln»): раздел
 * читает бизнес, а не разработчик.
 */
export const CONTENT_NETWORK_LABEL_RU = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    facebook: 'Facebook',
    threads: 'Threads',
};
/** Ярус силы клипа (строчными — стоит внутри строки таблицы рядом с кратностью). */
export const CONTENT_TIER_LABEL_RU = {
    strong: 'сильный',
    working: 'рабочий',
    weak: 'слабый',
    no_data: 'нет данных',
};
/** Правило выбора первого клипа в сети. */
export const CONTENT_RULE_LABEL_RU = {
    shared_ig: 'По статистике Instagram',
    own: 'По статистике этой сети',
    combined: 'Instagram + эта сеть',
};
/** Кто выбрал правило: человек (ночная проверка не трогает) или ночная проверка. */
export const CONTENT_RULE_MODES = ['manual', 'auto'];
export const CONTENT_RULE_MODE_LABEL_RU = {
    manual: 'ручной выбор',
    auto: 'автоматически',
};
/** Подпись режима по полю `manual` правила. */
export function contentRuleModeLabelRu(manual) {
    return CONTENT_RULE_MODE_LABEL_RU[manual ? 'manual' : 'auto'];
}
/**
 * Сеть, где движок клип НЕ выбирает (`optimized: false`, сейчас threads):
 * статистика собирается, правило не действует.
 */
export const CONTENT_COLLECT_ONLY_LABEL_RU = 'только сбор статистики';
/**
 * То же состояние длинной формой — для карточки сети на экране «Правила».
 * Выводится из короткой, а не пишется второй фразой: до выноса два экрана
 * подписывали `optimized === false` двумя разными текстами.
 */
export const CONTENT_COLLECT_ONLY_DETAIL_LABEL_RU = `${CONTENT_COLLECT_ONLY_LABEL_RU.charAt(0).toUpperCase()}${CONTENT_COLLECT_ONLY_LABEL_RU.slice(1)}, правило не применяется`;
/** Правило Instagram не переключается: остальные сети смотрят на его статистику. */
export const CONTENT_INSTAGRAM_RULE_LABEL_RU = 'Для Instagram правило всегда общее';
/** Вердикт ночной проверки «своя модель сети против общего правила». */
export const CONTENT_EVALUATION_VERDICT_LABEL_RU = {
    own_better: 'своя модель сети предсказывает лучше общего правила',
    not_better: 'своя модель сети пока не лучше общего правила',
    not_enough: 'пока мало данных для сравнения',
};
/**
 * Итог проверки одной фразой по вердикту, который посчитал admin-api
 * (`ContentRuleEvaluationDTO.verdict`). Число роликов добавляется только к
 * вердикту: при «мало данных» оно ничего не объясняет.
 */
export function contentEvaluationSummaryRu(evaluation) {
    const text = CONTENT_EVALUATION_VERDICT_LABEL_RU[evaluation.verdict];
    return evaluation.verdict === 'not_enough'
        ? text
        : `${text} (роликов в сравнении: ${evaluation.n})`;
}
/** Тренд обычного ролика аккаунта. */
export const CONTENT_TREND_LABEL_RU = {
    up: 'растёт',
    down: 'падает',
    flat: 'без изменений',
};
/** Ручное переопределение клипа — подписи совпадают с кнопками действий. */
export const CONTENT_OVERRIDE_KIND_LABEL_RU = {
    exclude: 'Не ставить первым',
    pin: 'Ставить первым',
    rest: 'Отдых',
};
/**
 * Ключ подписи публикации. `default` действует во всех сетях без своей
 * подписи; В КАКИХ именно — меняется от аккаунта к аккаунту, поэтому подпись
 * ключа короткая, а список сетей экран показывает рядом из `effectiveFor`.
 */
export const CONTENT_CAPTION_KEY_LABEL_RU = {
    default: 'Основная подпись',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube — заголовок',
    facebook: 'Facebook',
    threads: 'Threads',
};
/** Откуда взята действующая подпись публикации. */
export const CONTENT_CAPTION_SOURCE_LABEL_RU = {
    admin: 'изменено в админке',
    config: 'из конфига',
    none: 'не задано',
};
/** Почему ручной сбор статистики не запустился (`started: false`). */
export const CONTENT_COLLECTOR_SKIP_REASON_LABEL_RU = {
    already_running: 'Сбор не запущен: он уже идёт',
    not_configured: 'Сбор не запущен: у движка не настроен источник статистики (Metricool)',
};
export const CONTENT_ALERT_SEVERITY_LABEL_RU = {
    error: 'ошибка',
    warn: 'внимание',
    info: 'к сведению',
};
/**
 * Виды тревог движка (`kind` — строка движка, в контракте `string`): короткое
 * название над текстом тревоги. Список — по производителям движка
 * (`collector/health.ts`, `analyzer/alerts.ts`, `policy/brandNetworks.ts`,
 * планировщик); новый вид без подписи показывается как есть.
 */
export const CONTENT_ALERT_KIND_LABEL_RU = {
    collector_stale: 'Сбор статистики не идёт',
    metricool_auth: 'Metricool: нет доступа',
    snapshots_stale: 'Нет свежих просмотров',
    link_failed: 'Ролик не связан с постом',
    drift_down: 'Просмотры падают',
    views_vanished: 'Просмотры пропали',
    learner_stale: 'Ночная проверка не запускалась',
    learner_error: 'Ошибки ночной проверки',
    no_hits_4w: '4 недели без залёта',
    expectation_gap: 'Хуже ожидания',
    engine_fallback: 'Запасной путь публикации',
    // Публикатор: у бренда в Metricool пропала сеть — посты туда не уходят (error).
    network_missing: 'Metricool не видит сеть',
    analyzer_error: 'Сбой проверки',
    policy_switched: 'Сменилось правило',
    experiment_done: 'Эксперимент завершён',
};
export function contentAlertKindLabelRu(kind) {
    return CONTENT_ALERT_KIND_LABEL_RU[kind] ?? kind;
}
/** «Лучшие» и «худшие» — по кратности к обычному ролику аккаунта. */
export const CONTENT_PUBLICATION_SORT_LABEL_RU = {
    published_desc: 'Сначала новые',
    multiple_desc: 'Сначала лучшие',
    multiple_asc: 'Сначала худшие',
    // Сортировка по клику на заголовок колонки (направление — отдельно, `dir`).
    publishedAt: 'По дате публикации',
    views: 'По просмотрам',
    multiple: 'По кратности',
};
export const CONTENT_PERIOD_LABEL_RU = {
    '7d': '7 дней',
    '28d': '28 дней',
    '90d': '90 дней',
};
/** «Почему выбран этот клип» в карточке публикации. */
export const CONTENT_CHOICE_REASON_LABEL_RU = {
    strong: 'сильный клип',
    working: 'рабочий клип',
    probe: 'проба нового клипа',
    override: 'закреплён вручную',
    fallback: 'запасной вариант',
};
/**
 * Откуда у нового клипа предварительная оценка — короткий бейдж у кандидата в
 * «Почему выбран этот клип».
 */
export const CONTENT_PRIOR_SOURCE_LABEL_RU = {
    twin: 'пара аккаунта',
    description: 'похожие сцены',
};
/** Подписи полей формы параметров правила. */
export const CONTENT_RULE_PARAM_LABEL_RU = {
    strongMultiple: 'Сильный клип — от ×',
    hookRestDays: 'Пауза клипа на первом месте, дней',
    probeEvery: 'Проба нового клипа — раз в N роликов',
};
/** Подсказки под полями формы параметров — что параметр значит для бизнеса. */
export const CONTENT_RULE_PARAM_HINT_RU = {
    hookRestDays: 'Один и тот же клип не ставится первым чаще, чем раз в N дней — так топ разбавляется другими.',
    probeEvery: '0 — пробы выключены; новые клипы получают шанс с учётом похожих сцен и пары аккаунта.',
};
/** Короткие подписи тех же параметров в карточке сети (значение — рядом). */
export const CONTENT_RULE_PARAM_SHORT_LABEL_RU = {
    strongMultiple: 'Сильный клип — от',
    hookRestDays: 'Пауза клипа на первом месте',
    probeEvery: 'Проба нового клипа',
};
/** «Проба нового клипа» при `probeEvery = 0`. */
export const CONTENT_PROBE_OFF_LABEL_RU = 'выключена';
/** Подписи вариантов `Badge` для уровня тревоги — рядом с подписью, как у `risk-labels`. */
export const CONTENT_ALERT_SEVERITY_BADGE_VARIANT = {
    error: 'danger',
    warn: 'warning',
    info: 'info',
};
/** Состояние тревоги: открыта (`resolvedAt === null`) или решена. */
export const CONTENT_ALERT_STATES = ['open', 'resolved'];
export const CONTENT_ALERT_STATE_LABEL_RU = {
    open: 'открыта',
    resolved: 'решена',
};
/** Тревога без аккаунта и сети — про движок целиком. */
export const CONTENT_ALERT_SCOPE_ENGINE_LABEL_RU = 'весь движок';
/**
 * Пункты меню «что сделать с клипом» — длинная форма подписи переопределения:
 * у `rest` в меню сразу видно, что спросят число дней.
 */
export const CONTENT_OVERRIDE_ACTION_LABEL_RU = {
    exclude: `${CONTENT_OVERRIDE_KIND_LABEL_RU.exclude}…`,
    pin: `${CONTENT_OVERRIDE_KIND_LABEL_RU.pin}…`,
    rest: `${CONTENT_OVERRIDE_KIND_LABEL_RU.rest} на N дней…`,
};
/** Ролик ещё набирает просмотры — кратность предварительная. */
export const CONTENT_MATURING_LABEL_RU = 'набирает';
/** Кандидат на первое место на паузе после выхода первым: «пауза до 12.10». */
export const CONTENT_RESTING_LABEL_RU = 'пауза до';
export const CONTENT_MATURING_HINT_RU = 'Ролик ещё набирает просмотры — кратность предварительная.';
/** Ролик собран старой склейкой, потому что движок не смог его собрать. */
export const CONTENT_LEGACY_COMPOSE_LABEL_RU = 'Старая склейка';
export const CONTENT_LEGACY_COMPOSE_HINT_RU = 'Движок не смог собрать ролик — вышел ролик старой склейки.';
/** У публикации нет имени первого клипа. */
export const CONTENT_UNNAMED_CLIP_LABEL_RU = 'Без имени клипа';
/** Клип ни разу не стоял первым. */
export const CONTENT_CLIP_NEVER_HOOK_LABEL_RU = 'ни разу';
/** Имя клипа; клип удалён из папки — его id, чтобы его можно было найти в логах движка. */
export function contentClipNameRu(name, id) {
    return name ?? `клип удалён (${id})`;
}
/** Словоформы единиц раздела для `formatCount` / `pluralRu` из `@wit-support/ui`. */
export const CONTENT_NOUN_FORMS = {
    views: ['просмотр', 'просмотра', 'просмотров'],
    videos: ['ролик', 'ролика', 'роликов'],
    days: ['день', 'дня', 'дней'],
    hits: ['залёт', 'залёта', 'залётов'],
    flops: ['провал', 'провала', 'провалов'],
    accounts: ['аккаунт', 'аккаунта', 'аккаунтов'],
};
//# sourceMappingURL=content-labels.js.map